import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

import { localFormat } from 'common/utils/dateUtils';

import { ButtonBase, Box, Typography, Drawer } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { RiCalendarEventLine } from 'react-icons/ri';

import * as NewRentalSelectors from 'selectors/NewRentalSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';

import { useTranslation } from 'services/localization/useTranslation';
import TimeSelector from 'components/TimeSelector';
import { TimeSelectorArgs } from 'components/TimeSelector/types';

interface Props {
	value: string;
	onChange: (date: string) => void;
	selectorType: TimeSelectorArgs;
}

const StartTimeInput = (props: Props) => {
	const { t } = useTranslation();
	const classes = useStyles();
	const { value, onChange, selectorType } = props;
	const [isOpen, setIsOpen] = useState<boolean>(false);

	const products = useSelector(NewRentalSelectors.products);
	const startLocation = useSelector(NewRentalSelectors.startLocation);
	const endLocation = useSelector(NewRentalSelectors.endLocation);
	const duration = useSelector(ViewSelectors.newRentalDuration);

	const memoizedProducts = useMemo(() => products, [products]);
	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);

	const isEndTime = selectorType.type === 'endTime';

	const label = isEndTime
		? t('timeLocationSelector.end', 'Ends')
		: t('timeLocationSelector.start', 'Starts');
	const placeholder = isEndTime
		? t('timeLocationSelector.selectEndTime', 'Select end time')
		: t('timeLocationSelector.selectStartTime', 'Select start time');
	const calendarLabel = isEndTime
		? t('timeLocationSelector.selectEndDate', 'Select end date')
		: t('timeLocationSelector.selectStartDate', 'Select start date');

	return (
		<>
			<Typography variant="body2" className={classes.label}>
				{label}
			</Typography>
			<Box className={classes.button}>
				<ButtonBase onClick={() => setIsOpen(true)} style={{ width: '100%' }} focusRipple>
					<Box
						p={2}
						width="100%"
						display="flex"
						flexDirection="row"
						alignItems="center"
						justifyContent="flex-start"
					>
						<RiCalendarEventLine />
						<Box mr={1} />
						<Typography variant="body1">
							{value ? localFormat(value, 'ddd DD.MM. HH:mm', shopDateFormat) : placeholder}
						</Typography>
					</Box>
				</ButtonBase>
			</Box>
			{isOpen && (
				<Drawer anchor="bottom" open={isOpen}>
					<TimeSelector
						date={value}
						onDateChange={(changedMoment: moment.Moment) => onChange(changedMoment.toISOString())}
						selectorType={selectorType}
						onClose={() => setIsOpen(false)}
						products={memoizedProducts}
						startLocationId={startLocation.id}
						endLocationId={endLocation.id}
						label={calendarLabel}
						duration={duration}
						minDate={moment()}
					/>
				</Drawer>
			)}
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		button: {
			width: '100%',
			height: '44px',
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			backgroundColor: theme.palette.background.paper,
		},
		label: {
			marginTop: 4,
			marginBottom: 4,
			color: theme.palette.text.inverse.primary,
		},
	}),
);

export default StartTimeInput;
